import { useMemo, useState, type ReactNode } from "react";
import {
  ArcElement,
  Chart as ChartJS,
  Legend,
  Tooltip,
  type ChartData,
} from "chart.js";
import { Doughnut } from "react-chartjs-2";
import { AlertTriangle, Coins, Info, ReceiptText } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import {
  billingChannelOf,
  channelLabel,
  channelFundingLabel,
  channelsFromProviders,
  fundingOf,
  fundingSplit,
  hasRecordedChannel,
  type FundingKey,
  type FundingSplit,
} from "../costs/fundingSource";
import {
  COST_CLASS_LABELS,
  MARKETING_COST_DOMAIN,
  WHATSAPP_COST_DOMAIN,
  costClassOf,
} from "../costs/costClass";
import type {
  EchoflicksSessionCostLineItem,
  EchoflicksVideoSession,
  ImageHdConsumptionOutcome,
} from "../types";
import { EmptyHint } from "./EchoflicksSessionList";
import {
  EVIDENCE_MATCHED,
  classTotal,
  formatEur,
  formatEurMinor,
  formatFx,
  formatMarketingDetail,
  formatWhatsAppDetail,
  hasWhatsAppSummary,
  isReconciledWithMeta,
  poolLabel,
  purposeLabel,
  rowsOfClass,
  whatsAppEur,
} from "./echoMessagingCosts";
import { formatDateTime } from "./sessionUtils";

ChartJS.register(ArcElement, Tooltip, Legend);

const CHART_COLORS = [
  "#2563eb",
  "#16a34a",
  "#f97316",
  "#9333ea",
  "#dc2626",
  "#0891b2",
  "#ca8a04",
  "#475569",
];

type CostView = "delivered" | "retries";

/**
 * Plain-language spend categories. The raw `cost_domain` is developer shorthand
 * (i2v / llm / …); this maps it to what the money actually bought, and — crucially —
 * splits the "llm" bucket into AI that *looked at pixels* (frame/photo analysis) vs
 * AI that *wrote text* (the director's scripting models). `short` fits the table badge.
 */
export type CostCategory = { key: string; label: string; short: string };

// Gemini calls in the llm domain that analyse images/video frames (vs. writing text).
// Matches the composer QC/vision + media-analysis node names, with a keyword fallback.
const VISION_LLM_NODES = new Set([
  "bbox",
  "semantic_qc",
  "i2v_qc",
  "i2v_prompt_correction",
  "title_bleed_assess",
  "title_bleed_shorten",
  "media_analysis",
]);

export function isVisionLlmNode(node?: string | null): boolean {
  if (!node) return false;
  if (VISION_LLM_NODES.has(node)) return true;
  return /bbox|qc|vision|crop|media|face|frame|image/i.test(node);
}

/**
 * Poster (IGP-046): everything one poster costs, as one category. The image
 * attempts (`poster_generation`) and the two planner LLM calls that pick the
 * source photos and write the prompt. Planner rows match by ledger node, so
 * rows written before the orchestrator tagged them still group, or by
 * `usage_detail.lane`, which the orchestrator sets from IGP-042 on.
 */
export const POSTER_CATEGORY: CostCategory = {
  key: "poster",
  label: "Poster",
  short: "Poster",
};

const POSTER_LANE = "poster_generation";
const POSTER_PLANNING_NODES = new Set([
  "poster_source_selection",
  "poster_prompt_composition",
]);

/** A poster image attempt (billed, or estimated when it never answered). */
export function isPosterImageCost(
  cost: Pick<EchoflicksSessionCostLineItem, "cost_domain">,
): boolean {
  return cost.cost_domain === "poster_generation";
}

/** A poster planner call: old rows by node name, new rows by lane too. */
export function isPosterPlanningCost(
  cost: Pick<EchoflicksSessionCostLineItem, "cost_domain" | "usage_detail">,
): boolean {
  if (isPosterImageCost(cost)) return false;
  if (cost.cost_domain === "poster_planning") return true;
  const detail = cost.usage_detail;
  return (
    POSTER_PLANNING_NODES.has(detail?.node ?? "") ||
    detail?.lane === POSTER_LANE
  );
}

export function isPosterCost(
  cost: Pick<EchoflicksSessionCostLineItem, "cost_domain" | "usage_detail">,
): boolean {
  return isPosterImageCost(cost) || isPosterPlanningCost(cost);
}

/**
 * A call that timed out or was cancelled before the provider answered, priced
 * as an estimate because the provider likely billed it (WR-058). The indexer
 * copies the ledger's `disposition` into `confidence` when it has no other.
 */
export function isUnbilledEstimate(
  cost: Pick<EchoflicksSessionCostLineItem, "confidence" | "disposition">,
): boolean {
  return (
    cost.disposition === "estimated_unbilled" ||
    cost.confidence === "estimated_unbilled"
  );
}

export function costCategory(
  cost: EchoflicksSessionCostLineItem,
): CostCategory {
  // Before the domain switch: a poster's planner rows are `llm` and would
  // otherwise land in Text, its image rows in Image generation.
  if (isPosterCost(cost)) return POSTER_CATEGORY;
  const domain = cost.cost_domain ?? "i2v"; // legacy rows predate cost_domain
  switch (domain) {
    case "i2v":
      return { key: "video", label: "Video generation", short: "Video" };
    case "music":
    case "music_plan":
      return { key: "music", label: "Music", short: "Music" };
    case "voice":
      return { key: "voice", label: "Voiceover", short: "Voice" };
    case "title_render":
      return { key: "titles", label: "Titles", short: "Titles" };
    case "image_gen":
    case "continuity_keyframe":
    case "day_arc_relight":
      return { key: "images", label: "Image generation", short: "Images" };
    case "image_hd_upscaling":
      return {
        key: "image_hd_upscaling",
        label: "Image HD upscaling",
        short: "HD upscale",
      };
    case "portrait_person_retake":
      return {
        key: "portrait_person_retake",
        label: "Portrait reframing",
        short: "Portrait retake",
      };
    case "llm":
      return isVisionLlmNode(cost.usage_detail?.node)
        ? { key: "vision", label: "Image analysis (AI)", short: "Analysis" }
        : { key: "text", label: "Text & scriptwriting (AI)", short: "Text" };
    // EMC-070: Meta's per-message fees and allocated acquisition spend.
    case WHATSAPP_COST_DOMAIN:
      return {
        key: "whatsapp",
        label: "WhatsApp messages",
        short: "WhatsApp",
      };
    case MARKETING_COST_DOMAIN:
      return {
        key: "marketing",
        label: "Marketing (allocated)",
        short: "Marketing",
      };
    default:
      return { key: domain, label: humanize(domain), short: humanize(domain) };
  }
}

export type CategorySummary = CostCategory & { total: number; count: number };

/** Distinct categories present in a set of line items, richest first. */
export function categoriesPresent(
  costs: EchoflicksSessionCostLineItem[],
): CategorySummary[] {
  const map = new Map<string, CategorySummary>();
  for (const cost of costs) {
    const cat = costCategory(cost);
    const entry = map.get(cat.key) ?? { ...cat, total: 0, count: 0 };
    const value = recordedCost(cost);
    if (cost.billable !== false && value != null) entry.total += value;
    entry.count += 1;
    map.set(cat.key, entry);
  }
  return [...map.values()].sort(
    (a, b) => b.total - a.total || b.count - a.count,
  );
}

/** The card's one Poster line (IGP-046). */
export type PosterCostSummary = {
  /** Priced, billable poster rows; null when none of them has a price. */
  totalUsd: number | null;
  imageUsd: number;
  planningUsd: number;
  /** One ledger row per billed or probably billed image attempt. */
  imageAttempts: number;
  planningCalls: number;
  /** Image attempts that never answered, priced as an estimate. */
  unbilledEstimates: number;
  /** Billable poster rows without a dollar figure. */
  unpricedCount: number;
  /** "openai gpt-image-2", plus the plane when the row records it. */
  imageRoutes: string[];
};

/**
 * Every poster row of the Echo, delivered or not, as one line. Each row is
 * counted once: the same rows sit in the Poster category, never in Text or
 * Image generation, so the category totals still add up to the Echo total.
 */
export function summarizePosterCosts(
  costs: EchoflicksSessionCostLineItem[],
): PosterCostSummary | null {
  const summary: PosterCostSummary = {
    totalUsd: null,
    imageUsd: 0,
    planningUsd: 0,
    imageAttempts: 0,
    planningCalls: 0,
    unbilledEstimates: 0,
    unpricedCount: 0,
    imageRoutes: [],
  };
  const routes = new Set<string>();
  let rows = 0;
  for (const cost of costs) {
    if (!isPosterCost(cost)) continue;
    rows += 1;
    const image = isPosterImageCost(cost);
    if (image) {
      summary.imageAttempts += 1;
      if (isUnbilledEstimate(cost)) summary.unbilledEstimates += 1;
      const route = posterImageRoute(cost);
      if (route) routes.add(route);
    } else {
      summary.planningCalls += 1;
    }
    if (cost.billable === false) continue;
    const value = recordedCost(cost);
    if (value == null) {
      summary.unpricedCount += 1;
      continue;
    }
    summary.totalUsd = (summary.totalUsd ?? 0) + value;
    if (image) summary.imageUsd += value;
    else summary.planningUsd += value;
  }
  if (!rows) return null;
  summary.imageRoutes = [...routes];
  return summary;
}

function posterImageRoute(cost: EchoflicksSessionCostLineItem): string | null {
  const detail = cost.usage_detail;
  const provider = cost.provider_id ?? detail?.provider ?? null;
  const model = cost.model_id ?? detail?.model_id ?? null;
  const name = [provider ? humanize(provider) : null, model]
    .filter(Boolean)
    .join(" ");
  if (!name) return null;
  const plane = detail?.plane?.trim();
  return plane ? `${name} (${humanize(plane)})` : name;
}

/**
 * i2v lifecycle statuses where the attempt did NOT make it into the final video:
 * rejected by QC or errored out. Used to peel retries/failures out of the default
 * "delivered" view. Non-i2v records (llm/music/voice/title) have no such status and
 * are always treated as delivered.
 */
const NON_DELIVERED_LIFECYCLE = new Set([
  "failed",
  "cancelled",
  // The orchestrator writes `timed_out` (plan 2026-10-22 HDW, gotcha L17);
  // `timeout` stays for older rows.
  "timed_out",
  "timeout",
  "qc_rejected",
  "download_failed",
]);

/** The ledger's HD restoration domain (CRM category "hd"). */
export const HD_COST_DOMAIN = "image_hd_upscaling";

/** HDW contract v1 §5: the card's label for each HD outcome. */
export const HD_OUTCOME_LABELS: Record<ImageHdConsumptionOutcome, string> = {
  used_in_film: "Used in film",
  bound_not_in_film: "Working copy, not in film",
  unused: "Not used",
  pending: "Pending",
  unattributed: "Not measured",
};

/** HDW contract v1 §5: a restoration the binding gate skipped. */
export const HD_AVOIDED_LABEL = "Skipped, not billed";

/** HD outcomes that never reached a film: shown beside the retries. */
const HD_NOT_IN_FILM_OUTCOMES = new Set(["unused", "bound_not_in_film"]);

/**
 * A $0 row the gate wrote for a call it skipped (HDW contract v1 §2). Its
 * avoided estimate lives in `usage_detail` and is never added to a total.
 */
export function isAvoidedByPolicy(
  cost: Pick<EchoflicksSessionCostLineItem, "billable" | "disposition">,
): boolean {
  return cost.billable === false && cost.disposition === "avoided_by_policy";
}

/** The label of an HD row's outcome, or null for other rows and old rows. */
export function hdOutcomeLabel(
  cost: Pick<
    EchoflicksSessionCostLineItem,
    "cost_domain" | "consumption_outcome"
  >,
): string | null {
  if (cost.cost_domain !== HD_COST_DOMAIN) return null;
  const outcome = cost.consumption_outcome;
  if (!outcome) return null;
  return (
    HD_OUTCOME_LABELS[outcome as ImageHdConsumptionOutcome] ?? humanize(outcome)
  );
}

/** `usage_detail.avoided_estimate_usd` in dollars; null when "unknown". */
export function avoidedEstimateUsd(
  cost: Pick<EchoflicksSessionCostLineItem, "usage_detail">,
): number | null {
  const raw = cost.usage_detail?.avoided_estimate_usd;
  if (raw == null || raw === "" || raw === "unknown") return null;
  const value = Number(raw);
  return Number.isFinite(value) && value >= 0 ? value : null;
}

/** "$0.08" for an estimate: cents are enough, it is not a bill. */
function formatAboutUsd(value: number) {
  if (value > 0 && value < 0.01) return "<$0.01";
  return `$${value.toFixed(2)}`;
}

/** "(would have cost about $0.08)"; null when the estimate is unknown. */
export function avoidedEstimateLabel(
  cost: Pick<EchoflicksSessionCostLineItem, "usage_detail">,
): string | null {
  const value = avoidedEstimateUsd(cost);
  return value == null
    ? null
    : `(would have cost about ${formatAboutUsd(value)})`;
}

/** The Outcome column: HD labels first, then QC or lifecycle status. */
export function costOutcomeLabel(cost: EchoflicksSessionCostLineItem): string {
  if (isAvoidedByPolicy(cost)) return HD_AVOIDED_LABEL;
  return (
    hdOutcomeLabel(cost) ??
    humanize(cost.qc_disposition ?? cost.lifecycle_status ?? "reported")
  );
}

/**
 * A cost line item counts as "delivered" unless it's a rejected/failed
 * attempt, an HD restoration no film used, or a call skipped by policy.
 */
export function isDeliveredCost(cost: EchoflicksSessionCostLineItem): boolean {
  // A failed WhatsApp message or a marketing allocation is not a production
  // retry: the retries view is about generation attempts only.
  if (costClassOf(cost.cost_domain) !== "production") return true;
  if (isAvoidedByPolicy(cost)) return false;
  if (
    cost.cost_domain === HD_COST_DOMAIN &&
    HD_NOT_IN_FILM_OUTCOMES.has(cost.consumption_outcome ?? "")
  ) {
    return false;
  }
  if (
    cost.qc_disposition === "rejected" ||
    cost.qc_disposition === "not_uploaded"
  ) {
    return false;
  }
  if (
    cost.lifecycle_status &&
    NON_DELIVERED_LIFECYCLE.has(cost.lifecycle_status)
  ) {
    return false;
  }
  return true;
}

/** One HD outcome of the Echo: dollars and paid restorations. */
export type HdOutcomeFigure = {
  outcome: ImageHdConsumptionOutcome;
  label: string;
  usd: number;
  count: number;
};

/** The HD line of the card, from the profile fields (HDW contract v1 §4). */
export type HdOutcomeSummary = {
  /** Every outcome, in the contract's order; zero ones included. */
  outcomes: HdOutcomeFigure[];
  /** Sum of the outcomes: what HD restoration billed. */
  billedUsd: number;
  /** Gate skips: an estimate, never in a total. */
  avoidedCount: number;
  avoidedUsd: number;
  basis: string;
};

const HD_OUTCOME_ORDER: ImageHdConsumptionOutcome[] = [
  "used_in_film",
  "bound_not_in_film",
  "unused",
  "pending",
  "unattributed",
];

function finiteAmount(value: unknown): number {
  const number = typeof value === "number" ? value : Number(value);
  return Number.isFinite(number) && number > 0 ? number : 0;
}

/**
 * Used / unused / avoided HD restoration of one Echo, or null when the
 * session predates HDW-023 (none of the profile fields is present).
 */
export function summarizeHdOutcomes(
  session: Pick<
    EchoflicksVideoSession,
    | "echo_cost_image_hd_by_outcome"
    | "echo_cost_image_hd_outcome_counts"
    | "echo_cost_image_hd_avoided_usd"
    | "echo_cost_image_hd_avoided_count"
    | "echo_cost_image_hd_outcome_basis"
  >,
): HdOutcomeSummary | null {
  const byOutcome = session.echo_cost_image_hd_by_outcome;
  const counts = session.echo_cost_image_hd_outcome_counts;
  if (
    byOutcome == null &&
    counts == null &&
    session.echo_cost_image_hd_avoided_usd == null &&
    session.echo_cost_image_hd_avoided_count == null &&
    !session.echo_cost_image_hd_outcome_basis
  ) {
    return null;
  }
  const outcomes = HD_OUTCOME_ORDER.map((outcome) => ({
    outcome,
    label: HD_OUTCOME_LABELS[outcome],
    usd: finiteAmount(byOutcome?.[outcome]),
    count: finiteAmount(counts?.[outcome]),
  }));
  return {
    outcomes,
    billedUsd: outcomes.reduce((sum, entry) => sum + entry.usd, 0),
    avoidedCount: finiteAmount(session.echo_cost_image_hd_avoided_count),
    avoidedUsd: finiteAmount(session.echo_cost_image_hd_avoided_usd),
    basis: session.echo_cost_image_hd_outcome_basis ?? "none",
  };
}

const HD_BASIS_NOTES: Record<string, string> = {
  events:
    "Measured from the pipeline's reports of which restorations reached the film.",
  backfill_estimate:
    "Estimated by a backfill: this Echo ran before the pipeline reported HD outcomes.",
  none: "This Echo has no HD outcome reports.",
};

/** "Used in film $0.07 (1)": the summary line's parts, zero outcomes left out. */
export function hdOutcomeParts(summary: HdOutcomeSummary): string[] {
  const parts = summary.outcomes
    .filter(
      (entry) =>
        entry.outcome === "used_in_film" || entry.usd > 0 || entry.count > 0,
    )
    .map(
      (entry) =>
        `${entry.label} ${formatCostUsd(entry.usd)} (${formatInteger(entry.count)})`,
    );
  if (summary.avoidedCount > 0) {
    parts.push(
      summary.avoidedUsd > 0
        ? `${HD_AVOIDED_LABEL} (${formatInteger(summary.avoidedCount)}, would have cost about ${formatAboutUsd(summary.avoidedUsd)})`
        : `${HD_AVOIDED_LABEL} (${formatInteger(summary.avoidedCount)})`,
    );
  }
  return parts;
}

/** The card's HD restore line: used, unused and avoided (HDW-024). */
export const HdOutcomeStrip = ({ summary }: { summary: HdOutcomeSummary }) => (
  <div
    className="flex flex-wrap items-baseline gap-x-2 gap-y-1 rounded-md border p-3 text-sm"
    data-testid="echo-hd-outcomes"
  >
    <span className="font-semibold">{`HD restore ${formatCostUsd(
      summary.billedUsd,
    )}`}</span>
    {hdOutcomeParts(summary).map((part) => (
      <span key={part}>
        <span className="text-muted-foreground">· </span>
        {part}
      </span>
    ))}
    <span className="w-full text-xs text-muted-foreground">
      {HD_BASIS_NOTES[summary.basis] ?? HD_BASIS_NOTES.none} Skipped
      restorations were never billed: their cost is an estimate and is not in
      any total.
    </span>
  </div>
);

/** Pretty labels for the cost_domain discriminator (ECHO-COST-008). */
const DOMAIN_LABELS: Record<string, string> = {
  i2v: "I2V",
  llm: "LLM",
  music: "Music",
  voice: "Voice",
  title_render: "Titles",
  music_plan: "Music plan",
  image_gen: "Images",
  poster_generation: "Poster generation",
  poster_planning: "Poster planning",
  continuity_keyframe: "Continuity images",
  day_arc_relight: "Image relighting",
  image_hd_upscaling: "Image HD upscaling",
  portrait_person_retake: "Portrait reframing",
  media_qc: "Photo QC (vision checks)",
  whatsapp: "WhatsApp messages",
  marketing: "Marketing (allocated)",
  gpu_overhead: "Shared GPU overhead (allocated)",
  cloud_overhead: "Non-AWS cloud overhead (allocated)",
};

export function costDomainLabel(domain?: string | null) {
  const key = domain ?? "i2v"; // legacy rows predate cost_domain → i2v
  return DOMAIN_LABELS[key] ?? humanize(key);
}

export const EchoCostSection = ({
  session,
  costs: ledgerCosts,
  isLoading,
  showMarketing = false,
}: {
  session: EchoflicksVideoSession;
  costs: EchoflicksSessionCostLineItem[];
  isLoading?: boolean;
  /**
   * D6: allocated marketing is admin only. The API removes the rows and the
   * `echo_cost_acquisition_*` fields for everyone else; the card never shows
   * a marketing row or figure without this flag either way.
   */
  showMarketing?: boolean;
}) => {
  const costs = useMemo(
    () =>
      showMarketing
        ? ledgerCosts
        : ledgerCosts.filter(
            (cost) => costClassOf(cost.cost_domain) !== "acquisition",
          ),
    [ledgerCosts, showMarketing],
  );
  // Delivered = assets that ended up in the final video (plus every non-i2v
  // domain call). Retries, failures and unused = attempts that were rejected
  // or errored out, HD restorations no film used (HDW-024), and calls the
  // binding gate skipped (not billed). The default view shows only delivered so a retry-heavy or benchmark echo
  // doesn't read as alarming; the rest sits behind a tab.
  const delivered = useMemo(() => costs.filter(isDeliveredCost), [costs]);
  const retries = useMemo(
    () => costs.filter((cost) => !isDeliveredCost(cost)),
    [costs],
  );
  const hasRetries = retries.length > 0;
  const [viewMode, setViewMode] = useState<CostView>("delivered");
  const view = viewMode === "retries" && hasRetries ? "retries" : "delivered";
  const viewCosts = view === "retries" ? retries : delivered;

  // Category filter: Video / Image analysis / Text / Music / … "All" by default.
  // Splits the AI spend into what it actually did instead of the raw "domain".
  const categories = useMemo(() => categoriesPresent(viewCosts), [viewCosts]);
  const [categoryChoice, setCategoryChoice] = useState<string>("all");
  const category = categories.some((c) => c.key === categoryChoice)
    ? categoryChoice
    : "all";
  const tableCosts = useMemo(
    () =>
      category === "all"
        ? viewCosts
        : viewCosts.filter((cost) => costCategory(cost).key === category),
    [viewCosts, category],
  );

  // D1: the headline number is production; WhatsApp and marketing sit beside
  // it in the split line, so it stays comparable with every earlier Echo.
  const headline = resolveEchoCostHeadline(session, costs);
  const split = resolveEchoCostSplit(session, costs, { showMarketing });
  const showSplit = split.whatsapp != null || split.marketing != null;
  const deliveredTotal = totalFromCosts(rowsOfClass(delivered, "production"));
  const retryTotal = totalFromCosts(retries);
  const unknownCount = costs.length
    ? costs.filter(
        (cost) => cost.billable !== false && recordedCost(cost) == null,
      ).length
    : (session.echo_cost_unknown_count ?? session.i2v_cost_unknown_count ?? 0);
  const confidence =
    session.echo_cost_confidence ?? session.i2v_cost_confidence;
  const lineItemCount = costs.length
    ? costs.length
    : (session.echo_cost_line_item_count ??
      session.i2v_cost_line_item_count ??
      0);
  const requestCount = costs.length
    ? costs.filter((cost) => cost.billable !== false).length
    : (session.echo_cost_request_count ?? lineItemCount);
  const inputTokens = usageTokenTotal(
    costs,
    "input_tokens",
    session.echo_cost_input_tokens,
  );
  const outputTokens = usageTokenTotal(
    costs,
    "output_tokens",
    session.echo_cost_output_tokens,
  );
  const reasoningTokens = usageTokenTotal(
    costs,
    "reasoning_tokens",
    session.echo_cost_reasoning_tokens,
  );
  const cachedTokens = usageTokenTotal(
    costs,
    "cached_input_tokens",
    session.echo_cost_cached_input_tokens,
  );
  const imageTextInputTokens = usageTokenTotal(
    costs,
    "text_input_tokens",
    session.echo_cost_image_text_input_tokens,
  );
  const imageInputTokens = usageTokenTotal(
    costs,
    "image_input_tokens",
    session.echo_cost_image_input_tokens,
  );
  const imageOutputTokens = usageTokenTotal(
    costs,
    "image_output_tokens",
    session.echo_cost_image_output_tokens,
  );
  // Chart slices: by category on the "All" tab, by model once a category is
  // picked (so Video splits into Veo Standard / Veo Fast / Kling, Text into each model).
  const chartData = useMemo(
    () =>
      buildCostChartData(
        tableCosts,
        category === "all"
          ? (cost) => costCategory(cost).label
          : costModelLabel,
      ),
    [tableCosts, category],
  );
  const hasCostData = costs.length > 0 || headline.total != null;
  const funding = useMemo(
    () =>
      resolveFundingSplit(session, costs, {
        includeAcquisition: showMarketing,
      }),
    [session, costs, showMarketing],
  );
  // ECL C3: only a parent with edits or remakes carries the render rollup.
  const renderTotals = resolveRenderTotals(session, headline.total);
  // IGP-046: across both views, so a cancelled attempt stays on the line.
  const poster = useMemo(() => summarizePosterCosts(costs), [costs]);
  // HDW-024: used, unused and avoided HD restoration, from the profile.
  const hdOutcomes = summarizeHdOutcomes(session);

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle>Echo cost</CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              Ledger estimates for recorded requests. Production is what making
              the film cost; WhatsApp is what Meta charges for this Echo&apos;s
              messages
              {showMarketing
                ? "; marketing is the ad and WhatsApp marketing spend allocated to it"
                : ""}
              . H3 clips are priced from their measured GPU seconds. Shared
              infrastructure (AWS), RunPod boot and idle time, and clips without
              a worker receipt are excluded.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {headline.i2vOnly && hasCostData ? (
              <Badge variant="secondary">i2v only</Badge>
            ) : null}
            {confidence ? (
              <Badge variant={unknownCount ? "secondary" : "outline"}>
                {humanize(confidence)}
              </Badge>
            ) : null}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <EmptyHint>Loading echo costs...</EmptyHint>
        ) : !hasCostData ? (
          <EmptyHint>No cost data is linked to this Echo.</EmptyHint>
        ) : (
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
              <CostMetric
                icon={<Coins className="size-4" />}
                label="Production cost estimate"
                value={formatCostUsd(headline.total)}
                detail={
                  hasRetries
                    ? `${formatCostUsd(deliveredTotal)} delivered · ${formatCostUsd(
                        retryTotal,
                      )} retries and unused`
                    : undefined
                }
              />
              <CostMetric
                icon={<ReceiptText className="size-4" />}
                label="Provider requests"
                value={formatInteger(requestCount)}
                detail={`${formatInteger(lineItemCount)} ledger rows`}
              />
              <CostMetric
                icon={<AlertTriangle className="size-4" />}
                label="Unknown prices"
                value={String(unknownCount ?? 0)}
              />
              <CostMetric
                icon={<Info className="size-4" />}
                label="Text tokens"
                value={formatInteger((inputTokens ?? 0) + (outputTokens ?? 0))}
                detail={`${formatInteger(inputTokens)} in · ${formatInteger(outputTokens)} out`}
              />
              <CostMetric
                icon={<Info className="size-4" />}
                label="Reasoning & cache"
                value={`${formatInteger(reasoningTokens)} reasoning`}
                detail={`${formatInteger(cachedTokens)} cached input tokens`}
              />
              <CostMetric
                icon={<Info className="size-4" />}
                label="Image tokens"
                value={formatInteger(
                  (imageInputTokens ?? 0) + (imageOutputTokens ?? 0),
                )}
                detail={`${formatInteger(imageInputTokens)} in · ${formatInteger(imageOutputTokens)} out · ${formatInteger(imageTextInputTokens)} text prompt`}
              />
            </div>

            {showSplit ? <CostSplitStrip split={split} /> : null}

            {renderTotals ? <RenderTotalsStrip totals={renderTotals} /> : null}

            {funding ? <FundingStrip funding={funding} /> : null}

            {poster ? <PosterCostStrip summary={poster} /> : null}

            {hdOutcomes ? <HdOutcomeStrip summary={hdOutcomes} /> : null}

            {unknownCount ? (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                {unknownCount} cost item
                {unknownCount === 1 ? " lacks" : "s lack"} a dollar estimate.
                The recorded total is incomplete.
              </div>
            ) : null}

            {hasRetries ? (
              <div className="flex flex-wrap items-center gap-2">
                <div className="inline-flex rounded-md border p-0.5">
                  <Button
                    type="button"
                    size="sm"
                    variant={view === "delivered" ? "default" : "ghost"}
                    onClick={() => setViewMode("delivered")}
                  >
                    Delivered ({delivered.length})
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={view === "retries" ? "default" : "ghost"}
                    onClick={() => setViewMode("retries")}
                  >
                    Retries, failures and unused ({retries.length})
                  </Button>
                </div>
                {view === "retries" ? (
                  <span className="text-xs text-muted-foreground">
                    Extra generation attempts that didn&apos;t reach the final
                    video, HD restorations no film used, and restorations
                    skipped by policy (not billed). Provider billing for failed
                    attempts varies; QC-rejected attempts can still be charged.
                  </span>
                ) : null}
              </div>
            ) : null}

            <div className="flex flex-wrap gap-2">
              <CategoryTab
                label="All"
                active={category === "all"}
                onClick={() => setCategoryChoice("all")}
              />
              {categories.map((cat) => (
                <CategoryTab
                  key={cat.key}
                  label={cat.label}
                  total={cat.total}
                  active={category === cat.key}
                  onClick={() => setCategoryChoice(cat.key)}
                />
              ))}
            </div>

            <div className="grid gap-4 lg:grid-cols-[200px_minmax(0,1fr)]">
              <div className="space-y-3">
                <div className="mx-auto aspect-square max-w-[190px]">
                  {chartData.labels?.length ? (
                    <Doughnut
                      data={chartData}
                      options={{
                        maintainAspectRatio: true,
                        plugins: {
                          legend: {
                            position: "bottom",
                            labels: { boxWidth: 12, boxHeight: 12 },
                          },
                          tooltip: {
                            callbacks: {
                              label: (item) =>
                                `${item.label}: ${formatCostUsd(
                                  Number(item.raw),
                                )}`,
                            },
                          },
                        },
                      }}
                    />
                  ) : (
                    <EmptyHint>No priced slices to chart.</EmptyHint>
                  )}
                </div>
              </div>

              <div className="overflow-x-auto rounded-md border">
                <div className="min-w-[680px]">
                  <div className="grid grid-cols-[92px_minmax(220px,1.7fr)_minmax(160px,1fr)_136px_112px] gap-3 pr-4 bg-muted px-3 py-2 text-xs font-medium uppercase text-muted-foreground">
                    <div>Category</div>
                    <div>Item</div>
                    <div>Service</div>
                    <div>Outcome</div>
                    <div className="text-right">Cost</div>
                  </div>
                  {tableCosts.length ? (
                    tableCosts.map((cost) => (
                      <div
                        key={cost.id}
                        className="grid grid-cols-[92px_minmax(220px,1.7fr)_minmax(160px,1fr)_136px_112px] gap-3 pr-4 border-t px-3 py-2 text-sm"
                      >
                        <div className="min-w-0">
                          <Badge variant="outline">
                            {costCategory(cost).short}
                          </Badge>
                        </div>
                        <div className="min-w-0">
                          <div className="truncate font-medium">
                            {costItemLabel(cost)}
                          </div>
                          <div className="truncate text-xs text-muted-foreground">
                            {[
                              cost.provider_duration_seconds
                                ? `${cost.provider_duration_seconds}s`
                                : null,
                              billedGpuSecondsLabel(cost),
                              formatCredits(cost),
                              cost.attempt_number
                                ? `Attempt ${cost.attempt_number}`
                                : null,
                              formatDateTime(cost.occurred_at),
                            ]
                              .filter(Boolean)
                              .join(" / ")}
                          </div>
                          <UsageDetailLine cost={cost} />
                        </div>
                        <div className="min-w-0">
                          <div className="truncate">
                            {costProviderLabel(cost)}
                          </div>
                          <div className="truncate text-xs text-muted-foreground">
                            {costServiceDetail(cost)}
                          </div>
                          <FundingTag cost={cost} />
                        </div>
                        <div className="min-w-0">
                          <div
                            className="truncate"
                            title={costOutcomeLabel(cost)}
                          >
                            {costOutcomeLabel(cost)}
                          </div>
                          {isAvoidedByPolicy(cost) &&
                          avoidedEstimateLabel(cost) ? (
                            <div className="text-xs text-muted-foreground">
                              {avoidedEstimateLabel(cost)}
                            </div>
                          ) : null}
                        </div>
                        <div
                          className="text-right font-medium"
                          title={costPriceTooltip(cost)}
                        >
                          {cost.billable === false
                            ? "Not billed"
                            : formatCostUsd(recordedCost(cost))}
                          <div className="text-xs font-normal text-muted-foreground">
                            {costEvidenceLabel(cost)}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : costs.length ? (
                    <EmptyHint>
                      No{" "}
                      {view === "retries"
                        ? "retries, failures or unused"
                        : "delivered"}{" "}
                      line items{category === "all" ? "" : " in this category"}.
                    </EmptyHint>
                  ) : (
                    <EmptyHint>Summary-only historical cost data.</EmptyHint>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const CostMetric = ({
  icon,
  label,
  value,
  detail,
}: {
  icon: ReactNode;
  label: string;
  value: string;
  detail?: string;
}) => (
  <div className="rounded-md border p-3">
    <div className="flex items-center gap-2 text-xs font-medium uppercase text-muted-foreground">
      {icon}
      {label}
    </div>
    <div className="mt-2 truncate text-lg font-semibold">{value}</div>
    {detail ? (
      <div
        className="mt-1 truncate text-xs text-muted-foreground"
        title={detail}
      >
        {detail}
      </div>
    ) : null}
  </div>
);

const UsageDetailLine = ({ cost }: { cost: EchoflicksSessionCostLineItem }) => {
  const summary = formatCostDetail(cost);
  if (!summary) return null;
  return (
    <div className="truncate text-xs text-muted-foreground" title={summary}>
      {summary}
    </div>
  );
};

export type FundingResolution = {
  split: FundingSplit;
  /**
   * Where the channel amounts came from:
   * - `summary`: the indexer's `echo_cost_by_billing_channel`;
   * - `line_items`: each row's `usage_detail.billing_channel`, else its provider;
   * - `providers`: derived from `echo_cost_by_provider` (summary-only history).
   */
  source: "summary" | "line_items" | "providers";
  /** Some Google spend could not be placed on Vertex or AI Studio. */
  unknownGoogleUsd: number;
};

/**
 * Cash, credits and owned hardware for one Echo. Prefers the indexer's
 * billing-channel summary,
 * unless fresher line items disagree with it by a cent or more (the summary is
 * projected asynchronously — the same rule the headline total follows); then
 * the line items' own channels; then, for summary-only history, a derivation
 * from the provider map.
 */
export function resolveFundingSplit(
  session: Pick<
    EchoflicksVideoSession,
    "echo_cost_by_billing_channel" | "echo_cost_by_provider"
  >,
  costs: EchoflicksSessionCostLineItem[],
  {
    includeAcquisition = true,
  }: {
    /**
     * False for operators who are not admins (D6): the stored maps still
     * carry allocated Meta ads spend, so the `meta_ads` channel and provider
     * are left out. Their line items have no marketing rows already.
     */
    includeAcquisition?: boolean;
  } = {},
): FundingResolution | null {
  const withoutAds = <T,>(map: Record<string, T> | null | undefined) => {
    const out: Record<string, T> = { ...(map ?? {}) };
    if (!includeAcquisition) delete out[ACQUISITION_ONLY_CHANNEL];
    return out;
  };
  const summary = withoutAds(session.echo_cost_by_billing_channel);
  const lineItemChannels: Record<string, number> = {};
  for (const cost of costs) {
    if (cost.billable === false) continue;
    const value = recordedCost(cost);
    if (value == null) continue;
    const channel = billingChannelOf(cost);
    lineItemChannels[channel] = (lineItemChannels[channel] ?? 0) + value;
  }
  const sum = (map: Record<string, number | null | undefined>) =>
    Object.values(map).reduce<number>(
      (total, value) =>
        total +
        (typeof value === "number" && Number.isFinite(value) ? value : 0),
      0,
    );

  let byChannel: Record<string, number | null | undefined>;
  let source: FundingResolution["source"];
  if (
    Object.keys(summary).length &&
    (!costs.length || Math.abs(sum(summary) - sum(lineItemChannels)) < 0.01)
  ) {
    byChannel = summary;
    source = "summary";
  } else if (costs.length) {
    byChannel = lineItemChannels;
    source = "line_items";
  } else if (Object.keys(withoutAds(session.echo_cost_by_provider)).length) {
    byChannel = channelsFromProviders(
      withoutAds(session.echo_cost_by_provider),
    );
    source = "providers";
  } else {
    return null;
  }
  const split = fundingSplit(byChannel);
  if (!split.buckets.length) return null;
  const unknownGoogle = byChannel.google_unspecified;
  return {
    split,
    source,
    unknownGoogleUsd:
      typeof unknownGoogle === "number" && Number.isFinite(unknownGoogle)
        ? unknownGoogle
        : 0,
  };
}

/** Billing channel and provider id of allocated Meta ads spend (C4.3). */
const ACQUISITION_ONLY_CHANNEL = "meta_ads";

const FUNDING_COLORS: Record<FundingKey, string> = {
  google_cloud_credits: "bg-emerald-500",
  elevenlabs_credits: "bg-teal-400",
  cash: "bg-slate-500",
  cash_unknown_google: "bg-slate-400",
  owned_hardware: "bg-indigo-400",
};

const FUNDING_SOURCE_NOTES: Record<FundingResolution["source"], string> = {
  summary: "From the ledger's billing channel per request.",
  line_items:
    "From each request's billing channel, or its provider when the row predates the channel field.",
  providers:
    "Derived from the providers: this Echo predates billing channels in the ledger.",
};

/** Funding amounts read as money, not as per-token prices: two decimals. */
function formatFundingUsd(value: number) {
  if (value > 0 && value < 0.01) return "<$0.01";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

export const FundingStrip = ({ funding }: { funding: FundingResolution }) => {
  const { split } = funding;
  return (
    <div className="space-y-2 rounded-md border p-3" data-testid="echo-funding">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="text-xs font-medium uppercase text-muted-foreground">
          Who pays
        </div>
        <div className="text-sm">
          <span className="font-semibold">
            {formatFundingUsd(split.cash)} cash
          </span>
          <span className="text-muted-foreground">
            {" · "}
            {formatFundingUsd(split.credits)} credits
            {/* Owned-tower GPU time: only named when the Echo has some. */}
            {split.ownedHardware > 0
              ? ` · ${formatFundingUsd(split.ownedHardware)} owned hardware`
              : null}
          </span>
        </div>
      </div>
      <div className="flex h-2 w-full overflow-hidden rounded bg-muted">
        {split.buckets.map((bucket) => (
          <div
            key={bucket.key}
            className={FUNDING_COLORS[bucket.key]}
            style={{
              width: `${split.total ? (bucket.usd / split.total) * 100 : 0}%`,
            }}
            title={`${bucket.label}: ${formatFundingUsd(bucket.usd)}`}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs">
        {split.buckets.map((bucket) => (
          <span key={bucket.key} className="inline-flex items-center gap-1.5">
            <span
              className={`inline-block size-2 rounded-sm ${FUNDING_COLORS[bucket.key]}`}
            />
            {bucket.label} {formatFundingUsd(bucket.usd)}
            <span className="text-muted-foreground">
              ({split.total ? Math.round((bucket.usd / split.total) * 100) : 0}
              %)
            </span>
          </span>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        {FUNDING_SOURCE_NOTES[funding.source]}{" "}
        {funding.unknownGoogleUsd > 0
          ? `${formatFundingUsd(funding.unknownGoogleUsd)} of Google spend does not say whether it billed Vertex (credits) or AI Studio (cash) and is counted as cash. `
          : null}
        Vertex AI is paid from Google Cloud credits, ElevenLabs from ElevenLabs
        credits; everything else is cash, except GPU time on the owned towers,
        which is owned hardware (electricity plus depreciation: neither cash nor
        credits). H3 clips are priced from their measured GPU seconds: rented
        RunPod time is cash, owned-tower time is owned hardware, and a clip
        without a worker receipt stays &ldquo;not priced&rdquo;. RunPod boot and
        idle time and AWS are not in this card.
      </p>
    </div>
  );
};

const FundingTag = ({ cost }: { cost: EchoflicksSessionCostLineItem }) => {
  if (cost.billable === false) return null;
  const channel = billingChannelOf(cost);
  const recorded = hasRecordedChannel(cost);
  // An unpriced row (H3 clips without a worker receipt, mostly) has no dollar
  // to fund: naming a payer would read as a claim about money the ledger
  // never saw.
  const label =
    recordedCost(cost) == null
      ? `${channelLabel(channel)} · not priced`
      : channelFundingLabel(channel);
  const kind = fundingOf(channel).kind;
  return (
    <div
      className={`truncate text-xs ${
        kind === "credits"
          ? "text-emerald-700"
          : kind === "owned"
            ? "text-indigo-700"
            : "text-muted-foreground"
      }`}
      title={
        recorded
          ? `Billing channel recorded on the row: ${channelLabel(channel)}`
          : `No billing channel on the row; derived from the provider`
      }
    >
      {label}
    </div>
  );
};

/**
 * Headline total for the card: the production cost (D1). The cross-domain
 * echo total when the indexer has produced one, otherwise the legacy
 * i2v-only figure (badged "i2v only"). WhatsApp and marketing rows never
 * count here: `echo_cost_total_usd` is production only, and so is this.
 */
export function resolveEchoCostHeadline(
  session: Pick<
    EchoflicksVideoSession,
    "echo_cost_total_usd" | "i2v_cost_total_usd"
  >,
  costs: EchoflicksSessionCostLineItem[],
): { total: number | null; i2vOnly: boolean } {
  const production = rowsOfClass(costs, "production");
  const onlyI2v = (rows: EchoflicksSessionCostLineItem[]) =>
    rows.length > 0 &&
    rows.every((cost) => (cost.cost_domain ?? "i2v") === "i2v");
  // The relation endpoint returns every COST# row. Its fresh sum takes
  // precedence over an asynchronously projected SESSION summary.
  if (production.length) {
    return {
      total: totalFromCosts(production),
      i2vOnly: onlyI2v(production),
    };
  }
  const echoTotal = costNumber(session.echo_cost_total_usd);
  if (echoTotal != null) {
    return { total: echoTotal, i2vOnly: false };
  }
  const i2vTotal = costNumber(session.i2v_cost_total_usd);
  if (i2vTotal != null) {
    return { total: i2vTotal, i2vOnly: true };
  }
  // No summary and no production row: nothing to total.
  return { total: null, i2vOnly: !costs.length };
}

/** Production · WhatsApp · Marketing · All-in (EMC-071). */
export type EchoCostSplit = {
  production: number | null;
  /** Null when the Echo has no WhatsApp data at all (indexed before EMC). */
  whatsapp: number | null;
  /** Null unless the caller may see marketing (admin) and there is some. */
  marketing: number | null;
  /** The sum of the parts shown; null while production is unknown. */
  allIn: number | null;
};

/**
 * The headline split. Production is the card's headline number (so the two
 * always agree). WhatsApp and marketing come from the C3.7 class fields,
 * or from the rows of their class when a field is absent or the loaded rows
 * disagree with it by a cent or more.
 *
 * `showMarketing` is false for operators who are not admins (D6): marketing
 * is never shown to them, and All-in is the sum of the parts shown rather
 * than the stored `echo_cost_all_in_usd`, so it cannot reveal marketing
 * either.
 */
export function resolveEchoCostSplit(
  session: Pick<
    EchoflicksVideoSession,
    | "echo_cost_total_usd"
    | "i2v_cost_total_usd"
    | "echo_cost_messaging_usd"
    | "echo_cost_acquisition_usd"
    | "echo_cost_whatsapp_messages"
  >,
  costs: EchoflicksSessionCostLineItem[],
  { showMarketing }: { showMarketing: boolean },
): EchoCostSplit {
  const production = resolveEchoCostHeadline(session, costs).total;
  const rowsLoaded = costs.length > 0;

  const messagingRows = rowsOfClass(costs, "messaging");
  const whatsapp =
    messagingRows.length || hasWhatsAppSummary(session)
      ? // Only free messages: $0, not unknown.
        (classTotal(
          session.echo_cost_messaging_usd,
          messagingRows,
          rowsLoaded,
        ) ?? 0)
      : null;

  const acquisitionRows = showMarketing
    ? rowsOfClass(costs, "acquisition")
    : [];
  const acquisitionField = showMarketing
    ? costNumber(session.echo_cost_acquisition_usd)
    : null;
  const marketing =
    acquisitionRows.length || acquisitionField != null
      ? (classTotal(acquisitionField, acquisitionRows, rowsLoaded) ?? 0)
      : null;

  return {
    production,
    whatsapp,
    marketing,
    allIn:
      production == null
        ? null
        : production + (whatsapp ?? 0) + (marketing ?? 0),
  };
}

const CostSplitStrip = ({ split }: { split: EchoCostSplit }) => (
  <div
    className="flex flex-wrap items-baseline gap-x-2 gap-y-1 rounded-md border p-3 text-sm"
    data-testid="echo-cost-split"
  >
    <span>{`${COST_CLASS_LABELS.production} ${formatCostUsd(split.production)}`}</span>
    {split.whatsapp != null ? (
      <>
        <span className="text-muted-foreground">·</span>
        <span>{`${COST_CLASS_LABELS.messaging} ${formatCostUsd(split.whatsapp)}`}</span>
      </>
    ) : null}
    {split.marketing != null ? (
      <>
        <span className="text-muted-foreground">·</span>
        <span>{`${COST_CLASS_LABELS.acquisition} ${formatCostUsd(split.marketing)}`}</span>
      </>
    ) : null}
    <span className="text-muted-foreground">·</span>
    <span className="font-semibold">{`All-in ${formatCostUsd(split.allIn)}`}</span>
  </div>
);

/** First render, edits and remakes, all renders (ECL contract C3). */
export type RenderTotals = {
  firstUsd: number | null;
  derivativesUsd: number | null;
  derivativesCount: number;
  derivativesUnknownCount: number;
  allUsd: number | null;
};

/**
 * The render headline of a parent Echo, or null when it has no derivative
 * render (`echo_cost_derivatives_count` absent or 0: the card is unchanged).
 *
 * `firstRenderUsd` is the total the card already shows for this session's own
 * rows (fresh line items over the stored summary); the strip adds the stored
 * derivatives to it so the three figures always agree on screen. Without it
 * the stored `echo_cost_first_render_usd` / `echo_cost_all_renders_usd` hold.
 */
export function resolveRenderTotals(
  session: Pick<
    EchoflicksVideoSession,
    | "echo_cost_first_render_usd"
    | "echo_cost_derivatives_usd"
    | "echo_cost_derivatives_count"
    | "echo_cost_derivatives_unknown_count"
    | "echo_cost_all_renders_usd"
  >,
  firstRenderUsd: number | null,
): RenderTotals | null {
  const count = costNumber(session.echo_cost_derivatives_count);
  if (count == null || count <= 0) return null;
  const derivativesUsd = costNumber(session.echo_cost_derivatives_usd);
  const firstUsd =
    costNumber(firstRenderUsd) ??
    costNumber(session.echo_cost_first_render_usd);
  const allUsd =
    costNumber(firstRenderUsd) != null && derivativesUsd != null
      ? (firstUsd ?? 0) + derivativesUsd
      : (costNumber(session.echo_cost_all_renders_usd) ??
        (firstUsd != null && derivativesUsd != null
          ? firstUsd + derivativesUsd
          : null));
  return {
    firstUsd,
    derivativesUsd,
    derivativesCount: count,
    derivativesUnknownCount: Math.max(
      0,
      costNumber(session.echo_cost_derivatives_unknown_count) ?? 0,
    ),
    allUsd,
  };
}

const RenderTotalsStrip = ({ totals }: { totals: RenderTotals }) => (
  <div
    className="flex flex-wrap items-baseline gap-x-2 gap-y-1 rounded-md border p-3 text-sm"
    data-testid="echo-render-totals"
  >
    <span>{`First render ${formatCostUsd(totals.firstUsd)}`}</span>
    <span className="text-muted-foreground">·</span>
    <span>
      {`Edits and remakes ${formatCostUsd(totals.derivativesUsd)} (${formatInteger(
        totals.derivativesCount,
      )})`}
    </span>
    <span className="text-muted-foreground">·</span>
    <span className="font-semibold">
      {`All renders ${formatCostUsd(totals.allUsd)}`}
    </span>
    {totals.derivativesUnknownCount ? (
      <span className="text-xs text-amber-700 dark:text-amber-300">
        {`${formatInteger(totals.derivativesUnknownCount)} ${
          totals.derivativesUnknownCount === 1 ? "item" : "items"
        } in the edits and remakes ${
          totals.derivativesUnknownCount === 1 ? "has" : "have"
        } no price yet.`}
      </span>
    ) : null}
  </div>
);

function countLabel(count: number, one: string, many: string) {
  return `${formatInteger(count)} ${count === 1 ? one : many}`;
}

/** "2 image attempts $0.21"; a zero count has no dollar figure to show. */
function countWithCost(count: number, one: string, many: string, usd: number) {
  const label = countLabel(count, one, many);
  return count ? `${label} ${formatCostUsd(usd)}` : label;
}

/**
 * Poster (IGP-046): the image attempts and the planning calls of the poster,
 * their total, the image route, and how many attempts are estimates.
 */
export const PosterCostStrip = ({
  summary,
}: {
  summary: PosterCostSummary;
}) => (
  <div
    className="flex flex-wrap items-baseline gap-x-2 gap-y-1 rounded-md border p-3 text-sm"
    data-testid="echo-poster-cost"
  >
    <span className="font-semibold">{`Poster ${formatCostUsd(
      summary.totalUsd,
    )}`}</span>
    <span className="text-muted-foreground">·</span>
    <span>
      {countWithCost(
        summary.imageAttempts,
        "image attempt",
        "image attempts",
        summary.imageUsd,
      )}
    </span>
    {summary.unbilledEstimates ? (
      <span
        className="text-xs text-amber-700 dark:text-amber-300"
        title="Timed out or cancelled before the provider answered. Providers usually still bill these calls, so the ledger prices them as an estimate."
      >
        {`(${formatInteger(summary.unbilledEstimates)} estimated: no response, likely billed)`}
      </span>
    ) : null}
    <span className="text-muted-foreground">·</span>
    <span>
      {countWithCost(
        summary.planningCalls,
        "planning call",
        "planning calls",
        summary.planningUsd,
      )}
    </span>
    {summary.imageRoutes.length ? (
      <>
        <span className="text-muted-foreground">·</span>
        <span className="text-muted-foreground">
          {summary.imageRoutes.join(", ")}
        </span>
      </>
    ) : null}
    {summary.unpricedCount ? (
      <span className="text-xs text-amber-700 dark:text-amber-300">
        {`${countLabel(summary.unpricedCount, "poster item has", "poster items have")} no price yet.`}
      </span>
    ) : null}
  </div>
);

/**
 * The sub-label under a row's cost: where the dollar figure came from, in the
 * ECL evidence vocabulary (contract C1). Empty for a row that is not billed.
 */
export function costEvidenceLabel(cost: EchoflicksSessionCostLineItem): string {
  if (cost.billable === false) return "";
  if (cost.provider_reported_cost_usd != null) return "Provider reported";
  const value = recordedCost(cost);
  if (value == null) return "Unpriced";
  // EMC: a WhatsApp row is priced from Meta's rate card until Meta's own
  // daily report matches its cell; a marketing row is an allocation.
  const costClass = costClassOf(cost.cost_domain);
  if (costClass === "messaging") {
    return isReconciledWithMeta(cost) ? EVIDENCE_MATCHED : "Meta rate card";
  }
  if (costClass === "acquisition" || cost.confidence === "allocated") {
    return cost.usage_detail?.allocation_status === "provisional"
      ? "Allocated, provisional"
      : "Allocated";
  }
  switch (cost.confidence) {
    case "estimated_lower_bound":
      return "Lower bound";
    // Provider-metered credits priced at the provider's own account rate
    // (C2 revision 2), or a credit estimate without the header (C1).
    case "priced_from_provider_credits":
    case "estimated_from_credits":
      return cost.provider_id && cost.provider_id.toLowerCase() !== "elevenlabs"
        ? "From provider credits"
        : "From ElevenLabs credits";
    case "exact_catalog":
      if (value === 0) return "Free";
      break;
    case "priced_by_rate_card":
      return "Estimated";
  }
  return isGpuTimePriced(cost) ? "Estimated from GPU time" : "Estimated";
}

/** "812 ElevenLabs credits": the provider's own meter for the call. */
export function formatCredits(
  cost: Pick<
    EchoflicksSessionCostLineItem,
    "provider_reported_credits" | "provider_id"
  >,
): string | null {
  const credits = costNumber(cost.provider_reported_credits);
  if (credits == null || credits < 0) return null;
  const who =
    cost.provider_id?.toLowerCase() === "elevenlabs"
      ? "ElevenLabs"
      : "provider";
  return `${formatInteger(credits)} ${who} credits`;
}

/**
 * "Corrected from $0.0812": the old composer list price the rate card
 * replaced (C2 revision 2 `usage_detail.repriced_from_usd`), or null.
 */
export function formatRepricedFrom(
  detail?: Record<string, string> | null,
): string | null {
  const raw = detail?.repriced_from_usd;
  if (raw == null || raw === "") return null;
  const usd = costNumber(Number(raw));
  return usd == null ? null : `Corrected from ${formatCostUsd(usd)}`;
}

/** The rate card row that priced a cost row (usage_detail or price_source). */
function rateCardId(cost: EchoflicksSessionCostLineItem): string | null {
  const fromDetail = cost.usage_detail?.rate_id?.trim();
  if (fromDetail) return fromDetail;
  const source = cost.price_source ?? "";
  if (!source.startsWith("dashboard.vendor_usage_pricing")) return null;
  const hash = source.indexOf("#");
  return hash >= 0 ? source.slice(hash + 1) || null : null;
}

/**
 * Hover text for a row's cost: the rate card and price version that priced
 * it, the provider credits it reported, and what a $0 or lower-bound figure
 * means. Undefined when there is nothing to add.
 */
export function costPriceTooltip(
  cost: EchoflicksSessionCostLineItem,
): string | undefined {
  if (cost.billable === false) return undefined;
  const parts: string[] = [];
  // EMC-070: Meta bills in EUR. The ledger books USD at the ECB rate of the
  // day; the native amount and the rate stay on the row.
  const native = nativeEurLabel(cost);
  if (native) parts.push(native);
  const rate = rateCardId(cost);
  if (rate) {
    parts.push(
      `Priced by rate card ${rate}${
        cost.price_version ? ` (${cost.price_version})` : ""
      }`,
    );
  }
  const credits = formatCredits(cost);
  if (credits) parts.push(`${credits} reported by the provider`);
  const corrected = formatRepricedFrom(cost.usage_detail);
  if (corrected) {
    const source = cost.usage_detail?.repriced_from_source?.trim();
    parts.push(source ? `${corrected} (${source})` : corrected);
  }
  const value = recordedCost(cost);
  if (cost.confidence === "exact_catalog" && value === 0) {
    parts.push(
      "The list price is $0: the provider does not charge for this call",
    );
  }
  if (cost.confidence === "estimated_lower_bound") {
    const upper = costNumber(Number(cost.usage_detail?.upper_bound_usd));
    parts.push(
      upper != null
        ? `Lower bound: the call was cut short; at most ${formatCostUsd(upper)} if billed in full`
        : "Lower bound: the call was cut short",
    );
  }
  return parts.length ? parts.join(" · ") : undefined;
}

/**
 * "€0.0166 at 1.0850 USD per EUR (ECB 2026-10-20) · Meta rate card 2026q4"
 * for a WhatsApp row, "€3.10 at ... · week 2026-W42" for a marketing row;
 * null for every other row.
 */
export function nativeEurLabel(
  cost: EchoflicksSessionCostLineItem,
): string | null {
  const costClass = costClassOf(cost.cost_domain);
  const detail = cost.usage_detail;
  if (costClass === "messaging") {
    const eur = whatsAppEur(cost);
    if (eur == null) return null;
    const fx = formatFx(detail);
    const card = detail?.card_id?.trim();
    return [
      `${formatEur(eur)}${fx ? ` at ${fx}` : ""}`,
      card ? `Meta rate card ${card}` : null,
    ]
      .filter(Boolean)
      .join(" · ");
  }
  if (costClass === "acquisition") {
    const minor = Number(detail?.amount_eur_minor);
    if (!detail?.amount_eur_minor || !Number.isFinite(minor)) return null;
    const fx = formatFx(detail);
    return `${formatEurMinor(minor)}${fx ? ` at ${fx}` : ""}`;
  }
  return null;
}

export function countDistinctDomains(costs: EchoflicksSessionCostLineItem[]) {
  return new Set(costs.map((cost) => cost.cost_domain ?? "i2v")).size;
}

/** Label a line item for a chart slice. Defaults to its plain-language category. */
type CostLabeler = (cost: EchoflicksSessionCostLineItem) => string;

export function buildCostChartData(
  costs: EchoflicksSessionCostLineItem[],
  labelOf: CostLabeler = (cost) => costCategory(cost).label,
): ChartData<"doughnut", number[], string> {
  const grouped = buildCostChartSlices(costs, labelOf);
  return {
    labels: grouped.map((slice) => slice.label),
    datasets: [
      {
        data: grouped.map((slice) => slice.value),
        backgroundColor: grouped.map(
          (_, index) => CHART_COLORS[index % CHART_COLORS.length],
        ),
        borderWidth: 1,
      },
    ],
  };
}

export function buildCostChartSlices(
  costs: EchoflicksSessionCostLineItem[],
  labelOf: CostLabeler = (cost) => costCategory(cost).label,
) {
  const grouped = new Map<string, number>();
  for (const cost of costs) {
    if (cost.billable === false) continue;
    const value = recordedCost(cost);
    if (value == null || value <= 0) continue;
    const key = labelOf(cost);
    grouped.set(key, (grouped.get(key) ?? 0) + value);
  }
  return [...grouped.entries()]
    .map(([label, value]) => ({ label, value }))
    .sort((left, right) => right.value - left.value);
}

const CategoryTab = ({
  label,
  total,
  active,
  onClick,
}: {
  label: string;
  total?: number;
  active: boolean;
  onClick: () => void;
}) => (
  <Button
    type="button"
    size="sm"
    variant={active ? "default" : "outline"}
    onClick={onClick}
  >
    {label}
    {total != null && total > 0 ? (
      <span
        className={
          active ? "ml-1.5 opacity-80" : "ml-1.5 text-muted-foreground"
        }
      >
        {formatCostUsd(total)}
      </span>
    ) : null}
  </Button>
);

export function formatCostUsd(value?: number | null) {
  const amount = costNumber(value);
  if (amount == null) return "Unknown";
  if (amount > 0 && amount < 0.01) return "<$0.01";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: amount < 1 ? 2 : 2,
    maximumFractionDigits: amount < 1 ? 4 : 2,
  }).format(amount);
}

export function veoTier(
  cost: Pick<
    EchoflicksSessionCostLineItem,
    "provider_id" | "provider_tier" | "model_id"
  >,
): "standard" | "fast" | "lite" | null {
  const model = cost.model_id?.toLowerCase() ?? "";
  const provider = cost.provider_id?.toLowerCase() ?? "";
  const savedTier = cost.provider_tier?.toLowerCase() ?? "";
  if (!model.includes("veo") && !provider.startsWith("veo_gemini")) return null;
  if (model.includes("lite")) return "lite";
  if (
    model.includes("fast") ||
    provider === "veo_gemini_fast" ||
    provider === "veo_gemini_lite" ||
    savedTier === "fast"
  ) {
    return "fast";
  }
  if (savedTier === "lite") return "lite";
  return "standard";
}

export function costModelLabel(cost: EchoflicksSessionCostLineItem) {
  const tier = veoTier(cost);
  if (tier) return `Veo 3.1 ${veoTierLabel(tier)}`;
  const model = cost.model_id ?? "unknown model";
  // The same Gemini model bills credits on Vertex and cash on AI Studio, so a
  // model slice must not merge the two once the row says which it was.
  if (hasRecordedChannel(cost)) {
    const channel = billingChannelOf(cost);
    if (channel === "google_vertex" || channel === "google_ai_studio") {
      return `${model} · ${channelLabel(channel)}`;
    }
  }
  return model;
}

function costProviderLabel(cost: EchoflicksSessionCostLineItem) {
  const tier = veoTier(cost);
  if (tier) return `Veo ${veoTierLabel(tier)}`;
  const costClass = costClassOf(cost.cost_domain);
  if (costClass === "acquisition") {
    return poolLabel(cost.usage_detail?.pool ?? cost.provider_id);
  }
  if (costClass === "messaging") return channelLabel("meta_whatsapp");
  return humanize(
    cost.provider_id ?? cost.concrete_provider_id ?? "unknown provider",
  );
}

function veoTierLabel(tier: "standard" | "fast" | "lite") {
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}

function costServiceDetail(cost: EchoflicksSessionCostLineItem) {
  const backend =
    cost.concrete_provider_id && cost.concrete_provider_id !== cost.provider_id
      ? ` · backend ${cost.concrete_provider_id}`
      : "";
  return `${cost.model_id ?? "unknown model"}${backend}`;
}

/** "284 s GPU": GPU seconds read as time, not as a bare quantity. */
export function formatGpuSeconds(value: unknown): string | null {
  if (value == null || value === "") return null;
  const seconds = Number(value);
  if (!Number.isFinite(seconds) || seconds < 0) return null;
  return `${new Intl.NumberFormat("en-US", {
    maximumFractionDigits: seconds < 10 ? 1 : 0,
  }).format(seconds)} s GPU`;
}

/** A row the ledger priced from measured GPU time (H3 clips, 2026-10-02). */
export function isGpuTimePriced(
  cost: Pick<
    EchoflicksSessionCostLineItem,
    "billable_unit" | "confidence" | "price_source"
  >,
): boolean {
  return (
    cost.billable_unit === "gpu_second" ||
    cost.confidence === "estimated_gpu_time" ||
    Boolean(cost.price_source?.startsWith("dashboard.gpu_time_pricing"))
  );
}

/**
 * The billed GPU seconds of a `gpu_second` row, for the row's meta line. Null
 * when `usage_detail.gpu_seconds` is there: the usage line already shows it.
 */
function billedGpuSecondsLabel(cost: EchoflicksSessionCostLineItem) {
  if (cost.billable_unit !== "gpu_second") return null;
  if (cost.usage_detail?.gpu_seconds) return null;
  return formatGpuSeconds(cost.billable_quantity);
}

/**
 * The row's meta line: a plain summary for WhatsApp and marketing rows
 * (their usage_detail holds many ids and amounts), else formatUsageDetail.
 */
export function formatCostDetail(
  cost: EchoflicksSessionCostLineItem,
): string | null {
  switch (costClassOf(cost.cost_domain)) {
    case "messaging":
      return formatWhatsAppDetail(cost.usage_detail);
    case "acquisition":
      return formatMarketingDetail(cost.usage_detail);
    default:
      return formatUsageDetail(cost.usage_detail);
  }
}

/** One-line "node / token" summary of the flat usage_detail map. */
export function formatUsageDetail(
  detail?: Record<string, string> | null,
): string | null {
  if (!detail) return null;
  const parts: string[] = [];
  if (detail.node) parts.push(`node ${detail.node}`);
  if (detail.input_tokens || detail.output_tokens) {
    parts.push(
      `${detail.input_tokens ?? "?"} in / ${detail.output_tokens ?? "?"} out tokens`,
    );
  }
  if (Number(detail.cached_input_tokens ?? 0) > 0) {
    parts.push(`${detail.cached_input_tokens} cached input`);
  }
  if (Number(detail.reasoning_tokens ?? 0) > 0) {
    parts.push(`${detail.reasoning_tokens} reasoning`);
  }
  if (detail.image_input_tokens || detail.image_output_tokens) {
    parts.push(
      `${detail.image_input_tokens ?? "?"} image in / ${detail.image_output_tokens ?? "?"} image out tokens`,
    );
  }
  if (detail.text_input_tokens) {
    parts.push(`${detail.text_input_tokens} text input tokens`);
  }
  // A GPU-time row: "284 s GPU · $1.513 per GPU-hour · rate <id>".
  const gpuSeconds = formatGpuSeconds(detail.gpu_seconds);
  if (gpuSeconds) parts.push(gpuSeconds);
  if (Number(detail.usd_per_gpu_hour) > 0) {
    parts.push(`$${Number(detail.usd_per_gpu_hour)} per GPU-hour`);
  }
  if (detail.gpu_rate_id) parts.push(`rate ${detail.gpu_rate_id}`);
  // A vendor usage rate card row (ECL C2): "rate el-tts-v4".
  if (detail.rate_id) parts.push(`rate ${detail.rate_id}`);
  // The rate card replaced an old composer list price (C2 revision 2).
  const corrected = formatRepricedFrom(detail);
  if (corrected) parts.push(corrected);
  const summarized = new Set([
    "node",
    "input_tokens",
    "cached_input_tokens",
    "uncached_input_tokens",
    "output_tokens",
    "reasoning_tokens",
    "visible_output_tokens",
    "total_tokens",
    "image_input_tokens",
    "image_output_tokens",
    "text_input_tokens",
    // Shown as the row's funding tag in the Service column instead.
    "billing_channel",
    "gpu_seconds",
    "usd_per_gpu_hour",
    "gpu_rate_id",
    "rate_id",
    // Which pricer filled the price: the rate id above already says so.
    "priced_by",
    // Shown as the row's tooltip on a lower-bound estimate instead.
    "upper_bound_usd",
    // "Corrected from $X" above; the source URL is in the row's tooltip.
    "repriced_from_usd",
    "repriced_from_source",
    // HDW contract v1: join keys and hashes (long, for machines), and the
    // avoided estimate, which the Outcome column shows.
    "storage_key",
    "request_fingerprint",
    "raw_source_sha256",
    "avoided_estimate_usd",
    "avoided_estimate_basis",
  ]);
  for (const [key, value] of Object.entries(detail)) {
    if (summarized.has(key) || key.includes("price_per_1m")) continue;
    parts.push(`${humanize(key)} ${value}`);
  }
  return parts.length ? parts.join(" · ") : null;
}

export function formatInteger(value?: number | null) {
  if (typeof value !== "number" || !Number.isFinite(value)) return "0";
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(
    value,
  );
}

function totalFromCosts(costs: EchoflicksSessionCostLineItem[]) {
  const values = costs
    .filter((cost) => cost.billable !== false)
    .map(recordedCost)
    .filter((value): value is number => value != null);
  if (!values.length) return null;
  return values.reduce((sum, value) => sum + value, 0);
}

function costItemLabel(cost: EchoflicksSessionCostLineItem) {
  const costClass = costClassOf(cost.cost_domain);
  if (costClass === "messaging") {
    return `WhatsApp: ${purposeLabel(cost.usage_detail?.purpose)}`;
  }
  if (costClass === "acquisition") {
    return (
      cost.usage_detail?.campaign_name ??
      cost.usage_detail?.campaign_key ??
      cost.model_id ??
      "Marketing allocation"
    );
  }
  return (
    cost.scene_label ??
    cost.usage_detail?.node ??
    humanize(cost.segment_type ?? `${cost.cost_domain ?? "i2v"} item`)
  );
}

function costNumber(value?: number | null) {
  if (typeof value !== "number" || !Number.isFinite(value)) return null;
  return value;
}

function recordedCost(cost: EchoflicksSessionCostLineItem) {
  return costNumber(
    cost.effective_cost_usd ??
      cost.provider_reported_cost_usd ??
      cost.estimated_cost_usd,
  );
}

function usageTokenTotal(
  costs: EchoflicksSessionCostLineItem[],
  field: string,
  stored?: number | null,
) {
  if (!costs.length) return stored ?? null;
  return costs.reduce((total, cost) => {
    if (cost.billable === false) return total;
    const value = Number(cost.usage_detail?.[field]);
    return Number.isFinite(value) && value >= 0 ? total + value : total;
  }, 0);
}

function humanize(value: string) {
  return value.replaceAll("_", " ").replaceAll("-", " ");
}
